import { useCallback, useMemo, useState } from 'react';
import { getLiteral } from 'utils/getLiteral';
import { REG_EX_EMAIL } from 'constants/Constants';

const REQUIRED_FIELDS = [
    'name',
    'senderEmail',
    'responseEmail',
    'nickName',
    'address',
    'city',
    'postalCode',
    'idCountry',
];

const FREE_EMAIL_DOMAINS = new Set([
    'gmail.com',
    'outlook.com',
    'yahoo.com',
    'hotmail.com',
    'live.com',
    'icloud.com',
]);

export function useSenderFormValidation(formData) {
    const [errors, setErrors] = useState({});

    const fromEmailDomain = useMemo(() => {
        const normalized = formData.senderEmail?.trim().toLowerCase();
        if (!normalized || !normalized.includes('@')) return null;
        const [, domain] = normalized.split('@');
        return domain || null;
    }, [formData.senderEmail]);

    const isFreeEmailDomain = useMemo(
        () => !!fromEmailDomain && FREE_EMAIL_DOMAINS.has(fromEmailDomain),
        [fromEmailDomain],
    );

    const getRequiredError = useCallback((value) => {
        if (value === null || value === undefined || value === '') {
            return getLiteral('helptext_required_field');
        }
        return null;
    }, []);

    // Regex compiled once
    const emailRegex = useMemo(() => new RegExp(REG_EX_EMAIL.source, 'i'), []);

    const validateEmail = useCallback(
        (value) => {
            if (!value) return null;
            return emailRegex.test(value.trim()) ? null : getLiteral('label_invalid_email');
        },
        [emailRegex],
    );

    const validateField = useCallback(
        (field, value) => {
            if (REQUIRED_FIELDS.includes(field)) {
                const requiredError = getRequiredError(value);
                if (requiredError) return requiredError;
            }

            if (field === 'senderEmail' || field === 'responseEmail') {
                return validateEmail(value);
            }

            return null;
        },
        [getRequiredError, validateEmail],
    );

    const validateForm = useCallback(
        (data) => {
            const validationErrors = {};

            REQUIRED_FIELDS.forEach((field) => {
                const fieldError = validateField(field, data[field]);
                if (fieldError) validationErrors[field] = fieldError;
            });

            const isValid = Object.keys(validationErrors).length === 0;

            return {
                isValid,
                errors: validationErrors,
            };
        },
        [validateField],
    );

    const updateFieldError = useCallback(
        (field, value) => {
            const fieldError = validateField(field, value);
            setErrors((prev) => {
                if (fieldError) {
                    return { ...prev, [field]: fieldError };
                }
                if (prev[field]) {
                    const { [field]: removed, ...rest } = prev;
                    return rest;
                }
                return prev;
            });
        },
        [validateField],
    );

    const validateAndUpdateField = useCallback(
        (field, value) => {
            const fieldError = validateField(field, value);
            setErrors((prev) => {
                if (fieldError) {
                    return { ...prev, [field]: fieldError };
                }
                if (prev[field]) {
                    const { [field]: removed, ...rest } = prev;
                    return rest;
                }
                return prev;
            });
            return fieldError;
        },
        [validateField],
    );

    const validateAllFields = useCallback(
        (data) => {
            const { isValid, errors: validationErrors } = validateForm(data);
            setErrors(validationErrors);
            return isValid;
        },
        [validateForm],
    );

    const clearErrors = useCallback(() => {
        setErrors({});
    }, []);

    const setExternalErrors = useCallback((externalErrors) => {
        setErrors((prev) => ({
            ...prev,
            ...externalErrors,
        }));
    }, []);

    const clearFieldErrors = useCallback((fields) => {
        setErrors((prev) => {
            const next = { ...prev };
            fields.forEach((field) => {
                delete next[field];
            });
            return next;
        });
    }, []);

    return {
        errors,
        REQUIRED_FIELDS,
        FREE_EMAIL_DOMAINS,
        fromEmailDomain,
        isFreeEmailDomain,
        validateField,
        validateForm,
        validateEmail,
        updateFieldError,
        validateAndUpdateField,
        validateAllFields,
        clearErrors,
        setExternalErrors,
        clearFieldErrors,
    };
}
